import { normalizeImportHeader } from './field-synonyms'

/**
 * Board-style spreadsheet exports (monday.com "Export board to Excel") are not
 * a table with headers in row 1. They look like:
 *
 *   row 1   Board title
 *   row 2   Group title            ← first group
 *   row 3   Name | Person | Status | Date     ← header row
 *   row 4+  items…
 *           (blank)
 *           Next group title
 *           Name | Person | Status | Date     ← header repeated per group
 *           items…
 *
 * Both import parsers (the wizard's and the server's) read the grid through
 * this function so the headers the user maps in the browser are exactly the
 * headers the server job re-derives. The group each item sat under becomes a
 * synthetic "Group" column, which maps onto task groups.
 *
 * Subitem blocks ("Subitems" header row followed by indented rows with an empty
 * first cell) are skipped: subitems are not imported in this version.
 *
 * The layout is inferred from monday.com's documented export and community
 * samples; it is NOT verified against a current export file.
 */
export interface BoardExportLayout {
  /** 0-based index of the first header row in the grid. */
  headerRowIndex: number
  /** Header names, with `Group` appended when the sheet has no such column. */
  headers: string[]
  /** True when `Group` was synthesized (its value comes from `group`). */
  groupColumnAdded: boolean
  /** Data rows (0-based grid indexes) with the group each belongs to. */
  rows: { index: number; group: string | null }[]
}

export const BOARD_EXPORT_GROUP_HEADER = 'Group'

const MAX_HEADER_SEARCH_ROWS = 6

function filledCount(row: readonly string[]): number {
  return row.filter(cell => cell.trim() !== '').length
}

function isSingleLabelRow(row: readonly string[]): boolean {
  return filledCount(row) === 1 && (row[0] ?? '').trim() !== ''
}

function looksLikeHeaderRow(row: readonly string[]): boolean {
  const first = normalizeImportHeader(row[0] ?? '')
  return (first === 'name' || first === 'item') && filledCount(row) >= 2
}

function sameRow(a: readonly string[], b: readonly string[]): boolean {
  const length = Math.max(a.length, b.length)
  for (let i = 0; i < length; i += 1) {
    if ((a[i] ?? '').trim() !== (b[i] ?? '').trim()) return false
  }
  return true
}

/**
 * Returns the board layout, or null for an ordinary sheet (headers in row 1).
 * `grid` is the sheet as display text, one array per row, blank rows included.
 */
export function locateBoardExportLayout(
  grid: readonly (readonly string[])[],
): BoardExportLayout | null {
  if (grid.length < 3) return null
  // An ordinary table: row 1 already is the header row.
  const firstRow = grid[0] ?? []
  if (filledCount(firstRow) !== 1) return null

  let headerRowIndex = -1
  for (
    let index = 1;
    index < Math.min(grid.length, MAX_HEADER_SEARCH_ROWS + 1);
    index += 1
  ) {
    if (looksLikeHeaderRow(grid[index] ?? [])) {
      headerRowIndex = index
      break
    }
  }
  if (headerRowIndex === -1) return null

  const headerRow = grid[headerRowIndex] ?? []
  const lastFilled = headerRow.reduce(
    (last, cell, index) => (cell.trim() !== '' ? index : last),
    0,
  )
  const headers = headerRow
    .slice(0, lastFilled + 1)
    .map((cell, index) => cell.trim() || `Column ${index + 1}`)
  const hasGroupColumn = headers.some(
    header => normalizeImportHeader(header) === 'group',
  )

  // The group banner sits directly above the header row, below the board
  // title (row 1 is the title, so a banner needs headerRowIndex ≥ 2).
  const banner = grid[headerRowIndex - 1] ?? []
  let group: string | null =
    headerRowIndex >= 2 && isSingleLabelRow(banner)
      ? (banner[0] ?? '').trim()
      : null

  const nextNonEmpty = (from: number): number => {
    for (let index = from; index < grid.length; index += 1) {
      if (filledCount(grid[index] ?? []) > 0) return index
    }
    return -1
  }

  const rows: BoardExportLayout['rows'] = []
  let inSubitems = false
  for (let index = headerRowIndex + 1; index < grid.length; index += 1) {
    const row = grid[index] ?? []
    if (filledCount(row) === 0) {
      inSubitems = false
      continue
    }
    if (sameRow(row, headerRow)) {
      inSubitems = false
      continue
    }
    const first = (row[0] ?? '').trim()
    if (normalizeImportHeader(first) === 'subitems') {
      inSubitems = true
      continue
    }
    if (inSubitems) {
      if (first === '') continue
      inSubitems = false
    }
    if (isSingleLabelRow(row)) {
      const next = nextNonEmpty(index + 1)
      if (next !== -1 && sameRow(grid[next] ?? [], headerRow)) {
        group = first
        continue
      }
    }
    rows.push({ index, group })
  }

  return {
    headerRowIndex,
    headers: hasGroupColumn ? headers : [...headers, BOARD_EXPORT_GROUP_HEADER],
    groupColumnAdded: !hasGroupColumn,
    rows,
  }
}
